// React related imports
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Components
import { Button, Header, Search, SlideOverFilters, Tabs } from '@GxT-Team/gxt-components';
import Layout from '../../components/layout';
import SvgIcon from '../../components/elements/SvgIcon';
import { PaginatedTable } from '../../components/PaginatedTable';
import { PaginatedTableProps } from '../../components/PaginatedTable/PaginatedTable.types';
import NonInteractiveDocumentsModal from '../../components/modals/NonInteractiveDocumentsModal';
import StartAuthoringModal from './components/modals/AuthoringModal/StartAuthoringModal';
import KeyValueModal from './components/modals/KeyValueModal';

// Hooks and Contexts
import { usePaginatedFilters } from '../../hooks/usePaginatedFilters';
import useModal from '../../hooks/useModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useStartAuthoring } from '../authoring/hooks/useStartAuthoring';
import { GeneralizationContext } from '../../contexts/GeneralizationContext';

// Services and APIs
import {
  getCatalogs,
  getMappingSessionSourceFiles,
  useMappingSessionAPI,
} from '../../services/api';

// Utilities
import defaultFilters from '../catalog/util/defaultCatalogTabsFilters';
import { generateColumnsByTab } from '../catalog/components/CatalogTable/utils/catalogColumns';
import { getFilterFetcher, mapToFilterOptions } from '../catalog/util/helpers';
import { notifyError } from '../../helpers/utils';
import { AddSourcesPreviousAction } from '../catalog/util/constants';
import { BaseItem } from '../../components/PaginatedTable/PaginatedTable.types';
import { getSourceDocumentsMenuItems, mergeDynamicSuggestions } from './utils/helpers';

import {
  DocumentTabs,
  ICategory,
  IGroupingVariables,
  ISourceDocument,
  SourceDocumentTabs,
} from '../../types';
import {
  CatalogFilterParams,
  CatalogSortParams,
  ProjectFilterParams,
  ProjectSortParams,
} from '../../services/api/types';
import { DataSourceTabs, ISqlQueryObj } from '../DataSource/types';
import { generateColumns } from '../DataSource/components/utils/DataSourceColumns';
import QueriesModal from '../DataSource/components/utils/QueriesModal/QueriesModal';
import { getDataSource } from '../../services/api/dataSource/dataSource';
import { DataSourceObj } from '../../services/api/dataSource/dataSource.types';
import axios from '../../helpers/AxiosConfig';
import TinyMceModal from '../catalog/components/Modal/TinyMceModal/TinyMceModal';

type AddSourceTableRow = ISourceDocument & BaseItem;

// Define interface for template variables
interface TemplateVariable {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const AddSource = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    sessionId: rawSessionId,
    templateType,
    previousAction,
    dynamicMappingPayload,
    useLLM,
  } = location.state || {};
  const sessionId = Number(rawSessionId);
  const isValidSessionId = Number.isFinite(sessionId) && sessionId > 0;
  const queryKeyValue = [
    previousAction === AddSourcesPreviousAction.DYNAMIC_MAPPING ? 'DYNAMIC_MAPPING' : 'SOURCE',
  ];

  useEffect(() => {
    if (!isValidSessionId) {
      notifyError('Invalid URL');
      console.error('Missing session id');
      navigate('/catalog');
    }
  }, [isValidSessionId, navigate]);

  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [selectedQueryRecords, setSelectedQueryRecords] = useState<string | undefined>();
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters(DocumentTabs.SOURCE));
  const [nonInteractiveDocuments, setNonInteractiveDocuments] = useState<ISourceDocument[]>([]);
  const [selectedQueryRecord, setSelectedQueryRecord] = useState<ISqlQueryObj>();
  const [keyValuePairs, setKeyValuePairs] = useState<Record<string, string>>({});
  const [templateVariables, setTemplateVariables] = useState<TemplateVariable[]>([]);
  const [activeTab, setActiveTab] = useState(SourceDocumentTabs.DOCUMENT);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const {
    filters,
    setSearchByKey,
    paginatedFilteredData,
    appliedFiltersCount,
    handlePageChange,
    handlePerPageChange,
    setFilters,
    setAppliedFiltersCount,
    handleColumnSort,
  } = usePaginatedFilters<CatalogFilterParams, CatalogSortParams>(
    useMemo(
      () => ({
        filters: defaultFilters(DocumentTabs.SOURCE),
        search: {
          query: '',
          columns: ['id', 'title'],
        },
        sort: { type: 'DESC', column: 'id' },
      }),
      [],
    ),
  );

  const {
    setSearchByKey: setQueriesSearchByKey,
    paginatedFilteredData: queriesPaginatedData,
    handlePageChange: handleQueriesPageChange,
    handlePerPageChange: handleQueriesPerPageChange,
    handleColumnSort: handleQueriesColumnSort,
  } = usePaginatedFilters<ProjectFilterParams, ProjectSortParams>(
    useMemo(
      () => ({
        search: {
          query: '',
          columns: ['id', 'name'],
        },
        sort: { type: 'DESC', column: 'id' },
      }),
      [],
    ),
  );

  const showDocuments = activeTab === SourceDocumentTabs.DOCUMENT;
  const showQueries = activeTab === SourceDocumentTabs.QUERY;

  const slideOverFilters = useModal();
  const {
    show: showAuthoringModal,
    closeModal: closeAuthoringModal,
    openModal: openAuthoringModal,
  } = useModal();

  const {
    show: showNonInteractiveDocumentModal,
    closeModal: closeNonInteractiveDocumentModal,
    openModal: openNonInteractiveDocumentModal,
  } = useModal();

  const {
    show: showKeyValueModal,
    closeModal: closeKeyValueModal,
    openModal: openKeyValueModal,
  } = useModal();

  const {
    show: showVariableModal,
    closeModal: closeVariableModal,
    openModal: openVariableModal,
  } = useModal();

  const {
    show: sourceDocumentModal,
    closeModal: closeSourceDocumentModal,
    openModal: openSourceDocumentModal,
  } = useModal();

  const { setAiResponse, setAiResponseCopy, setGVCategories, sanitizeAiResponse } =
    useContext(GeneralizationContext);

  const { addSourceFile, dynamicMappingSuggestions } = useMappingSessionAPI();
  const { mutateAsync: getDynamicSuggestions } = dynamicMappingSuggestions;

  const numSelectedRecords = useMemo(() => {
    return selectedRecords.map((id) => +id);
  }, [selectedRecords]);

  const { handleStartAuthoring, isPending } = useStartAuthoring({
    selectedRecords: numSelectedRecords,
    sessionId,
    openNonInteractiveDocumentModal,
    templateType,
    closeAuthoringModal,
    nonInteractiveDocuments,
    useLLM,
  });

  const handleSelectAll = useCallback(
    (records: (ISourceDocument & BaseItem)[], newSelection: string[]) => {
      if (activeTab === SourceDocumentTabs.DOCUMENT) {
        setSelectedRecords(newSelection);
        setNonInteractiveDocuments(
          records.filter((item) => !item.isInteractive && newSelection.includes(String(item.id))),
        );
      } else {
        setSelectedQueryRecords(undefined);
      }
    },
    [activeTab],
  );

  const handleSelect = (
    event: ChangeEvent<HTMLInputElement>,
    record: ISourceDocument & BaseItem,
  ) => {
    if (activeTab === SourceDocumentTabs.DOCUMENT) {
      if (event.target.checked) {
        setSelectedRecords((prev) => [...prev, event.target.value]);
        !record.isInteractive && setNonInteractiveDocuments((prev) => [...prev, record]);
      } else {
        setSelectedRecords((prev) => prev.filter((id) => id !== event.target.value));
        !record.isInteractive &&
          setNonInteractiveDocuments((prev) =>
            prev.filter((doc) => doc.id !== +event.target.value),
          );
      }
    }
  };

  const handleQuerySelect = (event: ChangeEvent<HTMLInputElement>, record: ISqlQueryObj) => {
    if (event.target.checked) {
      setSelectedQueryRecords(event.target.value);
      setSelectedQueryRecord(record);
    } else {
      setSelectedQueryRecords(undefined);
      setSelectedQueryRecord(undefined);
    }
  };

  const sortedColumn = useMemo(
    () => ({
      column: paginatedFilteredData.sort?.column || '',
      order: paginatedFilteredData.sort?.type || 'DESC',
    }),
    [paginatedFilteredData.sort?.column, paginatedFilteredData.sort?.type],
  );

  const columns = useMemo(
    () => generateColumnsByTab(DocumentTabs.SOURCE, handleColumnSort, sortedColumn),
    [handleColumnSort, sortedColumn],
  );

  const queriesSortedColumn = useMemo(
    () => ({
      column: queriesPaginatedData.sort?.column || '',
      order: queriesPaginatedData.sort?.type || 'DESC',
    }),
    [queriesPaginatedData.sort?.column, queriesPaginatedData.sort?.type],
  );

  const queriesColumns = useMemo(
    () => generateColumns(handleQueriesColumnSort, queriesSortedColumn),
    [handleQueriesColumnSort, queriesSortedColumn],
  );

  const getFilteredPaginatedCatalogs = () => {
    switch (previousAction) {
      case AddSourcesPreviousAction.DYNAMIC_MAPPING:
        if (!isValidSessionId) return Promise.resolve({ data: { result: [], metadata: {} } });
        return getMappingSessionSourceFiles(paginatedFilteredData, sessionId);
      default:
        return getCatalogs(paginatedFilteredData);
    }
  };

  const navigateToSession = () => navigate(`/mapping-sessions/${sessionId}`);

  const handleAddSource = () => {
    if (!isValidSessionId) return;

    addSourceFile
      .mutateAsync({
        sourceFileIds: selectedRecords.map((id) => Number(id)),
        sessionId,
      })
      .then(() => navigateToSession());
  };

  const fetchFilterOptions = async () => {
    try {
      const fetchFilterOptionsFn = getFilterFetcher(DocumentTabs.SOURCE);

      const { data } = await fetchFilterOptionsFn();
      return data;
    } catch (error) {
      console.error('Error fetching filterOptions data:', error);
      throw error;
    }
  };

  const filtersQuery = useQuery({
    queryFn: fetchFilterOptions,
    queryKey: ['SourceFilters'],
  });

  const handleApplyFilters = () => {
    setFilters(appliedFilters);

    const appliedFiltersCount = Object.values(filters || {}).reduce((sum: number, value) => {
      if (Array.isArray(value) && value.length) {
        return sum + 1;
      }
      return sum;
    }, 0);

    setAppliedFiltersCount(appliedFiltersCount);
    slideOverFilters.closeModal();
  };

  const handleResetFilters = () => {
    setAppliedFilters(defaultFilters(DocumentTabs.SOURCE));
  };

  const handleCloseFilters = () => {
    slideOverFilters.closeModal();
    setAppliedFilters(filters);
  };

  const filtersOptionsData = useMemo(() => {
    return mapToFilterOptions(filtersQuery.data, (filters) => {
      setAppliedFilters({
        ...filters,
        ...defaultFilters(DocumentTabs.SOURCE),
      });
    });
  }, [filtersQuery.data]);

  const handleRun = () => {
    if (!isValidSessionId) return;

    getDynamicSuggestions({
      sessionId,
      newTargetNodeId: dynamicMappingPayload.targetSelectionNodeId,
      suggestionId: dynamicMappingPayload.referenceSelectionSuggestionId,
      sourceIds: selectedRecords.map((id) => Number(id)),
    }).then((res) => {
      const groupingVariables: IGroupingVariables = res.data.groupingVariables;
      setGVCategories((prev: ICategory[]) =>
        prev.map((item) => {
          const additionalVariables = groupingVariables[item.title] || [];
          return {
            ...item,
            options: [...item.options, ...additionalVariables],
          };
        }),
      );
      const sanitizedAIResponse = sanitizeAiResponse(res.data.aiSuggestions);
      setAiResponse((prev) => mergeDynamicSuggestions(prev, sanitizedAIResponse));
      setAiResponseCopy((prev) => mergeDynamicSuggestions(prev, sanitizedAIResponse));
    });

    navigate(`/mapping-sessions/${sessionId}`, {
      state: { targetNodeId: dynamicMappingPayload.targetSelectionNodeId },
    });
  };

  const handleKeyValueSubmit = (data: Record<string, string>) => {
    setKeyValuePairs(data);

    if (previousAction === AddSourcesPreviousAction.AUTHORING_REQUEST) {
      openAuthoringModal();
    } else {
      getActionButtonProps(previousAction).onClick();
    }
  };

  // Function to handle primary action button click
  const handlePrimaryAction = () => {
    // If there are template variables, open the KeyValue modal
    // If not, skip directly to the next step
    if (templateVariableNames.length > 0) {
      openKeyValueModal();
    } else if (previousAction === AddSourcesPreviousAction.AUTHORING_REQUEST) {
      // Open authoring modal directly if no template variables
      openAuthoringModal();
    } else {
      // For other actions, just call the action
      getActionButtonProps(previousAction).onClick();
    }
  };

  const getActionButtonProps = (previousAction: AddSourcesPreviousAction) => {
    switch (previousAction) {
      case AddSourcesPreviousAction.DYNAMIC_MAPPING:
        return {
          disabled: !selectedRecords.length,
          onClick: handleRun,
          label: 'Run',
          dataTestId: 'run-btn',
        };
      case AddSourcesPreviousAction.AUTHORING_REQUEST:
        return {
          disabled: !selectedRecords.length || isPending,
          onClick: handlePrimaryAction,
          label: 'Continue',
          dataTestId: 'continue-btn',
        };
      default:
        return {
          disabled: !selectedRecords.length || addSourceFile.isPending,
          onClick: handleAddSource,
          label: 'Add',
          dataTestId: 'add-btn',
        };
    }
  };

  const tabs = useMemo(
    () => [
      {
        title: 'Document(s)',
        active: showDocuments,
        onClickHandler: () => handleActiveTab(SourceDocumentTabs.DOCUMENT),
      },
      {
        title: 'Queries',
        active: showQueries,
        onClickHandler: () => handleActiveTab(SourceDocumentTabs.QUERY),
      },
    ],
    [showDocuments, showQueries],
  );

  const handleActiveTab = (tab: SourceDocumentTabs) => {
    setActiveTab(tab);
  };

  const getFilteredPaginatedQueries = () => {
    return getDataSource(queriesPaginatedData as unknown as DataSourceObj);
  };

  const handleSourceAdded = (sourceId: string) => {
    setSelectedRecords((prev) => [...prev, sourceId]);
  };

  useQuery({
    queryKey: ['templateVariables', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];

      try {
        const { data } = await axios.get<TemplateVariable[]>(
          templateType === 'Automated'
            ? `/mappingSession/${sessionId}/template-variables`
            : `/catalogs/${sessionId}/template-variables`,
        );

        if (data && Array.isArray(data)) {
          setTemplateVariables(data);
        }

        return data;
      } catch (error) {
        console.error('Error fetching template variables:', error);
        return [];
      }
    },
    enabled: !!sessionId && (templateType === 'Automated' || useLLM),
  });

  const templateVariableNames = useMemo(() => {
    return templateVariables.map((variable) => variable.name);
  }, [templateVariables]);

  const handleViewClick = (row: { id: number }) => {
    setSelectedId(row?.id);
    openSourceDocumentModal();
  };

  const menuItems = getSourceDocumentsMenuItems(handleViewClick);

  return (
    <Layout>
      <Layout.Header>
        <Header>
          <Header.Heading>Add Source Document(s)</Header.Heading>
        </Header>
      </Layout.Header>
      <Layout.Body>
        <Tabs tabs={tabs} />
        <div className="flex w-full gap-2">
          <Search
            onChangeCallback={(term) => {
              if (activeTab === SourceDocumentTabs.QUERY) {
                setQueriesSearchByKey('query', term);
              } else {
                setSearchByKey('query', term);
              }
            }}
            width="100%"
            className="h-full"
          />
          <Button
            variant="secondary"
            onClick={slideOverFilters.openModal}
            data-testid="filter-btn"
            className="flex justify-between items-center gap-3 whitespace-nowrap">
            {`Filter (${appliedFiltersCount})`}
            <SvgIcon
              date-testid={'filter-icon-svg'}
              iconType={'filter-icon'}
              stroke={addSourceFile.isPending ? 'var(--neutral-200, #9CA3AF)' : undefined}
            />
          </Button>
        </div>
        {activeTab === 'Document' && (
          <PaginatedTable<AddSourceTableRow>
            additionalColumns={
              columns as unknown as PaginatedTableProps<AddSourceTableRow>['additionalColumns']
            }
            paginatedFilteredData={paginatedFilteredData}
            getData={
              getFilteredPaginatedCatalogs as unknown as PaginatedTableProps<AddSourceTableRow>['getData']
            }
            handleSelectAll={
              handleSelectAll as unknown as PaginatedTableProps<AddSourceTableRow>['handleSelectAll']
            }
            onSelect={handleSelect as unknown as PaginatedTableProps<AddSourceTableRow>['onSelect']}
            selectedData={selectedRecords}
            handlePageChange={handlePageChange}
            handlePerPageChange={handlePerPageChange}
            queryKey={queryKeyValue}
            actionsColumn
            menuItems={menuItems as unknown as PaginatedTableProps<AddSourceTableRow>['menuItems']}
          />
        )}

        {activeTab === 'Queries' && (
          <PaginatedTable
            additionalColumns={queriesColumns}
            paginatedFilteredData={queriesPaginatedData}
            getData={
              getFilteredPaginatedQueries as unknown as PaginatedTableProps<
                ISourceDocument & BaseItem
              >['getData']
            }
            onSelect={
              handleQuerySelect as unknown as (
                event: ChangeEvent<HTMLInputElement>,
                record: ISourceDocument & BaseItem,
              ) => void
            }
            selectedData={selectedQueryRecords ? [selectedQueryRecords] : []}
            handleSelectAll={handleSelectAll}
            handlePageChange={handleQueriesPageChange}
            handlePerPageChange={handleQueriesPerPageChange}
            queryKey={['dataSource', DataSourceTabs.DATA_SOURCE]}
            label="dataSource"
          />
        )}

        <div className="flex justify-end items-start self-stretch">
          <div className="flex justify-end items-start gap-3">
            <Button
              data-testid={
                activeTab !== 'Queries'
                  ? getActionButtonProps(previousAction).dataTestId
                  : 'queries-test-id'
              }
              variant="primary"
              disabled={
                activeTab !== 'Queries' ? getActionButtonProps(previousAction).disabled : false
              }
              onClick={
                activeTab !== 'Queries' ? () => handlePrimaryAction() : () => openVariableModal()
              }>
              {activeTab !== 'Queries' ? getActionButtonProps(previousAction).label : 'Add'}
            </Button>
            <Button
              data-testid="navigate-cancel"
              variant="secondary"
              onClick={
                templateType
                  ? () => navigate('/catalog')
                  : () => navigate(`/mapping-sessions/${sessionId}`)
              }>
              Cancel
            </Button>
          </div>
        </div>
        <SlideOverFilters
          isOpen={slideOverFilters.show}
          onCloseHandler={handleCloseFilters}
          onApplyHandler={handleApplyFilters}
          onResetHandler={handleResetFilters}
          filtersOptions={filtersOptionsData}
          applyButtonLabel="Filter"
          closeButtonLabel="Close"
          resetButtonLabel="Clear Selection"
          title="Filters"
        />
        <StartAuthoringModal
          isOpen={showAuthoringModal}
          handleClose={closeAuthoringModal}
          handleAuthoringSubmit={(data) => {
            const templateVariables = Object.entries(keyValuePairs).map(
              ([parameterName, userValue]) => {
                return { [parameterName]: userValue };
              },
            );
            handleStartAuthoring(data, templateVariables);
          }}
        />
        <KeyValueModal
          isOpen={showKeyValueModal}
          handleClose={closeKeyValueModal}
          handleSubmit={handleKeyValueSubmit}
          keyLabels={templateVariableNames.length ? templateVariableNames : []}
        />
        <QueriesModal
          isOpen={showVariableModal}
          handleClose={closeVariableModal}
          queries={selectedQueryRecord}
          sessionId={sessionId}
          openAuthoringModal={openAuthoringModal}
          previousAction={previousAction}
          onSourceAdded={handleSourceAdded}
        />
        <NonInteractiveDocumentsModal
          isOpen={showNonInteractiveDocumentModal}
          closeModal={closeNonInteractiveDocumentModal}
          documents={nonInteractiveDocuments}
        />
        {sourceDocumentModal && selectedId && (
          <TinyMceModal
            closeEditorModalOpen={closeSourceDocumentModal}
            editorModalOpen={sourceDocumentModal}
            key="catalog_preview"
            editorType="Session"
            catalogId={selectedId}
            isFileInteractive={true}
          />
        )}
      </Layout.Body>
    </Layout>
  );
};

export default AddSource;
